import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { 
  Users, Cake, Loader2, MessageCircle, MapPin, Briefcase, 
  PartyPopper, Gift, Sparkles, ArrowRight, Heart, Phone, GitBranch,
  Search, Eye, X, Check, BookOpen
} from "lucide-react";

const WelcomePage = () => {
  const [showEventPopup, setShowEventPopup] = useState(false);
  const [popupImage, setPopupImage] = useState("");
  const [popupTitle, setPopupTitle] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [showFamiliesDialog, setShowFamiliesDialog] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [allFamilies, setAllFamilies] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [familySearchQuery, setFamilySearchQuery] = useState("");
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [wishedUsers, setWishedUsers] = useState(new Set());
  const [wishDialog, setWishDialog] = useState({ open: false, user: null, type: "birthday" });
  const [wishMessage, setWishMessage] = useState("");
  const [wishSending, setWishSending] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchWelcomeStats();
    fetchPopup();
  }, []);

  const fetchPopup = async () => {
    try {
      const res = await api.get("/popup");
      if (res.data.has_popup && res.data.image) {
        setPopupImage(res.data.image);
        setPopupTitle(res.data.title || "Announcement");
        setShowEventPopup(true);
      }
    } catch (_e) { console.error("Failed to fetch popup:", _e); }
  };

  const fetchWelcomeStats = async () => {
    setLoading(true);
    try {
      const response = await api.get("/users/stats/welcome");
      setStats(response.data);
    } catch (_error) {
      console.error("Failed to load welcome stats");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (u) => {
    if (u.first_name) {
      return `${u.first_name[0]}${(u.last_name || "L")[0]}`.toUpperCase();
    }
    if (u.username) {
      return u.username.slice(0, 2).toUpperCase();
    }
    return u.phone?.slice(-2) || "??";
  };

  // Load already-wished users from backend on mount
  useEffect(() => {
    const loadWished = async () => {
      try {
        const res = await api.get("/wishes/sent-today");
        if (res.data?.wished_user_ids) {
          setWishedUsers(new Set(res.data.wished_user_ids));
        }
      } catch (_e) { /* first time — no wishes yet */ }
    };
    loadWished();
  }, []);

  const openWishDialog = (targetUser, type = "birthday") => {
    if (wishedUsers.has(targetUser.id)) return;
    const name = targetUser.first_name || targetUser.last_name || "Friend";
    const defaultMsg = type === "birthday"
      ? `Happy Birthday, ${name}! Wishing you a wonderful day filled with joy and happiness!`
      : `Happy Anniversary, ${name}! Wishing you many more years of love and togetherness!`;
    setWishMessage(defaultMsg);
    setWishDialog({ open: true, user: targetUser, type });
  };

  const sendWish = async () => {
    if (!wishDialog.user || !wishMessage.trim()) return;
    setWishSending(true);
    try {
      const convRes = await api.post("/conversations", { participant_id: wishDialog.user.id });
      const convId = convRes.data.id;
      await api.post(`/conversations/${convId}/messages`, { content: wishMessage.trim() });
      
      // Record the wish in backend to prevent duplicates
      await api.post("/wishes/record", { 
        wished_user_id: wishDialog.user.id, 
        type: wishDialog.type 
      });
      
      setWishedUsers(prev => new Set([...prev, wishDialog.user.id]));
      const name = wishDialog.user.first_name || wishDialog.user.last_name || "Friend";
      toast.success(`Wish sent to ${name}!`);
      setWishDialog({ open: false, user: null, type: "birthday" });
      setWishMessage("");
    } catch (_error) {
      toast.error("Failed to send wish. Please try again.");
    } finally {
      setWishSending(false);
    }
  };

  const fetchAllMembers = async () => {
    setMembersLoading(true);
    try {
      const response = await api.get("/users/verified/all");
      setAllMembers(response.data);
    } catch (_error) {
      toast.error("Failed to load members");
    } finally {
      setMembersLoading(false);
    }
  };

  const handleOpenMembersDialog = () => {
    setShowMembersDialog(true);
    if (allMembers.length === 0) {
      fetchAllMembers();
    }
  };

  const fetchAllFamilies = async () => {
    setFamiliesLoading(true);
    try {
      const response = await api.get("/family-trees/verified/all");
      setAllFamilies(response.data);
    } catch (_error) {
      toast.error("Failed to load families");
    } finally {
      setFamiliesLoading(false);
    }
  };

  const handleOpenFamiliesDialog = () => {
    setShowFamiliesDialog(true);
    if (allFamilies.length === 0) {
      fetchAllFamilies();
    }
  };

  const handleViewFamily = async (familyId) => {
    try {
      const response = await api.get(`/family-trees/view/${familyId}`);
      setSelectedFamily(response.data);
    } catch (_error) {
      toast.error("Failed to load family details");
    }
  };

  const filteredMembers = allMembers.filter(member => {
    const searchLower = searchQuery.toLowerCase();
    const fullName = [member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ").toLowerCase();
    return (
      fullName.includes(searchLower) ||
      member.member_code?.toLowerCase().includes(searchLower) ||
      member.phone?.includes(searchQuery) ||
      member.city?.toLowerCase().includes(searchLower)
    );
  });

  const filteredFamilies = allFamilies.filter(family => {
    const searchLower = familySearchQuery.toLowerCase();
    return (
      family.family_name?.toLowerCase().includes(searchLower) ||
      family.family_id?.toLowerCase().includes(searchLower)
    );
  });

  const formatPhoneDisplay = (phone) => {
    if (!phone) return "N/A";
    // Show first 3 and last 2 digits, mask the middle
    if (phone.length >= 10) {
      return `${phone.slice(0, 3)}****${phone.slice(-2)}`;
    }
    return phone;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  return (
    <Layout>
      {/* Event Announcement Popup */}
      {showEventPopup && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          data-testid="event-popup-overlay"
          onClick={() => setShowEventPopup(false)}
        >
          <div 
            className="relative max-w-lg w-[90vw] max-h-[85vh] animate-in fade-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowEventPopup(false)}
              className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white text-black shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
              data-testid="event-popup-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={popupImage}
              alt={popupTitle}
              className="w-full h-auto rounded-xl shadow-2xl"
              data-testid="event-popup-image"
            />
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto py-6 px-4" data-testid="welcome-page">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2">
            {getGreeting()}, {user?.first_name || user?.username || "Friend"}!
          </h1>
          <p className="text-muted-foreground">Welcome to LUNAWAT FOUNDATION Community</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Stats Card */}
            <Card className="mb-6 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-8">
                  <button 
                    onClick={handleOpenMembersDialog}
                    className="flex items-center gap-4 hover:bg-primary/10 p-2 rounded-lg transition-colors cursor-pointer"
                  >
                    <div className="p-3 rounded-full bg-primary/20">
                      <Users className="w-8 h-8 text-primary" />
                    </div>
                    <div className="text-center">
                      <p className="text-4xl font-bold text-primary">
                        {stats?.total_verified_users || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Verified Members
                      </p>
                      <p className="text-xs text-primary mt-1">
                        View All →
                      </p>
                    </div>
                  </button>
                  <div className="h-12 w-px bg-border" />
                  <button 
                    onClick={handleOpenFamiliesDialog}
                    className="flex items-center gap-4 hover:bg-teal-50 dark:hover:bg-teal-900/20 p-2 rounded-lg transition-colors cursor-pointer"
                  >
                    <div className="p-3 rounded-full bg-teal-100 dark:bg-teal-900/30">
                      <GitBranch className="w-8 h-8 text-teal-600" />
                    </div>
                    <div className="text-center">
                      <p className="text-4xl font-bold text-teal-600">
                        {stats?.total_verified_families || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Verified Families
                      </p>
                      <p className="text-xs text-teal-600 mt-1">
                        View All →
                      </p>
                    </div>
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Kuldevi Form Link */}
            <Card className="mb-6 border-orange-200 dark:border-orange-800/50 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-900/50">
                      <BookOpen className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-orange-800 dark:text-orange-200">कुलदेवी साक्ष्य संकलन</h3>
                      <p className="text-sm text-orange-600 dark:text-orange-400">Kuldevi Evidence Collection</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/kuldevi-form')}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="kuldevi-form-btn"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    फॉर्म भरें
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Birthday Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-pink-100 dark:bg-pink-900/30">
                  <Cake className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Birthdays Today</h2>
                  <p className="text-sm text-muted-foreground">
                    {stats?.birthday_count || 0} member{stats?.birthday_count !== 1 ? 's' : ''} celebrating today
                  </p>
                </div>
              </div>

              {stats?.birthday_users && stats.birthday_users.length > 0 ? (
                <div className="space-y-3">
                  {stats.birthday_users.map((u) => (
                    <Card key={u.id} className="border-pink-200 dark:border-pink-800/50 bg-gradient-to-r from-pink-50/50 to-orange-50/50 dark:from-pink-900/10 dark:to-orange-900/10">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <Avatar 
                              className="w-14 h-14 cursor-pointer ring-2 ring-pink-300 dark:ring-pink-600"
                              onClick={() => navigate(`/user/${u.id}`)}
                            >
                              <AvatarImage src={u.avatar} />
                              <AvatarFallback className="bg-pink-100 text-pink-600 font-semibold">
                                {getInitials(u)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="absolute -top-1 -right-1 p-1 bg-pink-500 rounded-full">
                              <PartyPopper className="w-3 h-3 text-white" />
                            </div>
                          </div>
                          
                          <div 
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => navigate(`/user/${u.id}`)}
                          >
                            <h3 className="font-semibold truncate">
                              {u.prefix && <span className="text-muted-foreground">{u.prefix}. </span>}
                              {u.first_name || u.username || "User"} {u.last_name || ""}
                            </h3>
                            <div className="flex items-center gap-1 text-sm text-pink-600 dark:text-pink-400">
                              <Gift className="w-3 h-3" />
                              <span>Birthday Today! 🎉</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                              {u.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {u.city}
                                </span>
                              )}
                              {u.occupation && (
                                <span className="flex items-center gap-1 capitalize">
                                  <Briefcase className="w-3 h-3" /> {u.occupation}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col gap-2">
                            <Button
                              variant={wishedUsers.has(u.id) ? "outline" : "default"}
                              size="sm"
                              className={wishedUsers.has(u.id) ? "border-green-500 text-green-600" : "bg-pink-500 hover:bg-pink-600"}
                              onClick={() => openWishDialog(u, "birthday")}
                              disabled={wishedUsers.has(u.id) || u.id === user?.id}
                              data-testid={`wish-birthday-${u.id}`}
                            >
                              {wishedUsers.has(u.id) ? (
                                <><Check className="w-4 h-4 mr-1" /> Wished</>
                              ) : (
                                <><MessageCircle className="w-4 h-4 mr-1" /> Wish</>
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <Cake className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No birthdays today</p>
                    <p className="text-sm">Check back tomorrow!</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Anniversary Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <Heart className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Anniversaries Today</h2>
                  <p className="text-sm text-muted-foreground">
                    {stats?.anniversary_count || 0} member{stats?.anniversary_count !== 1 ? 's' : ''} celebrating today
                  </p>
                </div>
              </div>

              {stats?.anniversary_users && stats.anniversary_users.length > 0 ? (
                <div className="space-y-3">
                  {stats.anniversary_users.map((u) => (
                    <Card key={u.id} className="border-red-200 dark:border-red-800/50 bg-gradient-to-r from-red-50/50 to-pink-50/50 dark:from-red-900/10 dark:to-pink-900/10">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <Avatar 
                              className="w-14 h-14 cursor-pointer ring-2 ring-red-300 dark:ring-red-600"
                              onClick={() => navigate(`/user/${u.id}`)}
                            >
                              <AvatarImage src={u.avatar} />
                              <AvatarFallback className="bg-red-100 text-red-600 font-semibold">
                                {getInitials(u)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="absolute -top-1 -right-1 p-1 bg-red-500 rounded-full">
                              <Heart className="w-3 h-3 text-white" />
                            </div>
                          </div>
                          
                          <div 
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => navigate(`/user/${u.id}`)}
                          >
                            <h3 className="font-semibold truncate">
                              {u.prefix && <span className="text-muted-foreground">{u.prefix}. </span>}
                              {u.first_name || u.username || "User"} {u.last_name || ""}
                            </h3>
                            <div className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                              <Heart className="w-3 h-3" />
                              <span>Anniversary Today! 💕</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                              {u.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {u.city}
                                </span>
                              )}
                              {u.occupation && (
                                <span className="flex items-center gap-1 capitalize">
                                  <Briefcase className="w-3 h-3" /> {u.occupation}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col gap-2">
                            <Button
                              variant={wishedUsers.has(u.id) ? "outline" : "default"}
                              size="sm"
                              className={wishedUsers.has(u.id) ? "border-green-500 text-green-600" : "bg-red-500 hover:bg-red-600"}
                              onClick={() => openWishDialog(u, "anniversary")}
                              disabled={wishedUsers.has(u.id) || u.id === user?.id}
                              data-testid={`wish-anniversary-${u.id}`}
                            >
                              {wishedUsers.has(u.id) ? (
                                <><Check className="w-4 h-4 mr-1" /> Wished</>
                              ) : (
                                <><MessageCircle className="w-4 h-4 mr-1" /> Wish</>
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No anniversaries today</p>
                    <p className="text-sm">Check back tomorrow!</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Helpline Section */}
            <Card className="mb-6 bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 border-green-200 dark:border-green-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-green-500">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Helpline Number</p>
                      <p className="text-xl font-bold text-green-700 dark:text-green-400">7070301098</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-green-500 text-green-700 hover:bg-green-50"
                    onClick={() => window.open('tel:7070301098')}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call Now
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Continue to Feed Button */}
            <Button 
              className="w-full h-12 text-base"
              onClick={() => navigate("/feed")}
            >
              Continue to Feed
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </>
        )}
      </div>

      {/* All Verified Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              All Verified Members ({allMembers.length})
            </DialogTitle>
          </DialogHeader>
          
          {/* Search Bar */}
          <div className="px-4 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, LF ID, phone, or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Members List */}
          <ScrollArea className="h-[60vh] px-4 pb-4">
            {membersLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{searchQuery ? "No members found matching your search" : "No verified members yet"}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredMembers.map((member) => (
                  <Card key={member.id} className="hover:bg-accent/50 transition-colors">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={member.avatar} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                            {getInitials(member)}
                          </AvatarFallback>
                        </Avatar>

                        {/* Member Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold truncate">
                              {[member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ") || "Member"}
                            </p>
                            <span className="text-xs font-mono bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                              {member.member_code || "N/A"}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {formatPhoneDisplay(member.phone)}
                            </span>
                            {member.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {member.city}
                              </span>
                            )}
                            {member.business_type && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3 h-3" />
                                {member.business_type}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* View Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowMembersDialog(false);
                            navigate(`/user/${member.id}`);
                          }}
                          className="shrink-0"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* All Verified Families Dialog */}
      <Dialog open={showFamiliesDialog} onOpenChange={(open) => {
        setShowFamiliesDialog(open);
        if (!open) setSelectedFamily(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-teal-600" />
              {selectedFamily ? (
                <span className="flex items-center gap-2">
                  <button 
                    onClick={() => setSelectedFamily(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    ← Back
                  </button>
                  <span className="text-muted-foreground">/</span>
                  {selectedFamily.family_name}
                </span>
              ) : (
                `All Verified Families (${allFamilies.length})`
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedFamily ? (
            /* Family Detail View */
            <ScrollArea className="h-[65vh] px-4 pb-4">
              <div className="space-y-4">
                {/* Family Info Header */}
                <Card className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 border-teal-200 dark:border-teal-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-500">
                        <GitBranch className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-teal-700 dark:text-teal-300">
                          {selectedFamily.family_name}
                        </h3>
                        <p className="text-sm font-mono text-teal-600 dark:text-teal-400">
                          {selectedFamily.family_id}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedFamily.members?.length || 0} family members
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Family Members List */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
                    Family Members
                  </h4>
                  <div className="space-y-2">
                    {selectedFamily.members?.map((member, idx) => (
                      <Card key={member.user_id || idx} className="hover:bg-accent/50 transition-colors">
                        <CardContent className="p-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={member.avatar} />
                              <AvatarFallback className="bg-teal-100 text-teal-600 font-semibold text-sm">
                                {member.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium truncate">{member.name || "Member"}</p>
                                <span className="text-xs font-mono bg-teal-100 dark:bg-teal-900/50 text-teal-600 px-1.5 py-0.5 rounded">
                                  {member.lf_id || "N/A"}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {member.relationship}
                              </p>
                            </div>
                            {member.user_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setShowFamiliesDialog(false);
                                  setSelectedFamily(null);
                                  navigate(`/user/${member.user_id}`);
                                }}
                                className="shrink-0"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          ) : (
            /* Families List View */
            <>
              {/* Search Bar */}
              <div className="px-4 py-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by family name or FAM ID..."
                    value={familySearchQuery}
                    onChange={(e) => setFamilySearchQuery(e.target.value)}
                    className="pl-10"
                  />
                  {familySearchQuery && (
                    <button
                      onClick={() => setFamilySearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Families List */}
              <ScrollArea className="h-[60vh] px-4 pb-4">
                {familiesLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                  </div>
                ) : filteredFamilies.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{familySearchQuery ? "No families found matching your search" : "No verified families yet"}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredFamilies.map((family) => (
                      <Card key={family.id} className="hover:bg-accent/50 transition-colors">
                        <CardContent className="p-3">
                          <div className="flex items-center gap-3">
                            {/* Family Icon */}
                            <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
                              <GitBranch className="w-6 h-6 text-teal-600" />
                            </div>

                            {/* Family Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold truncate">
                                  {family.family_name || "Unnamed Family"}
                                </p>
                                <span className="text-xs font-mono bg-teal-100 dark:bg-teal-900/50 text-teal-600 px-1.5 py-0.5 rounded">
                                  {family.family_id}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {family.members?.length || 0} members
                              </p>
                            </div>

                            {/* View Button */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewFamily(family.family_id)}
                              className="shrink-0 border-teal-300 text-teal-600 hover:bg-teal-50"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Wish Message Dialog */}
      {wishDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setWishDialog({ open: false, user: null, type: "birthday" })}>
          <div className="bg-background rounded-xl shadow-2xl w-[90vw] max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="wish-dialog">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                {wishDialog.type === "birthday" ? <Cake className="w-5 h-5 text-pink-500" /> : <Heart className="w-5 h-5 text-red-500" />}
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  Send {wishDialog.type === "birthday" ? "Birthday" : "Anniversary"} Wish
                </h3>
                <p className="text-sm text-muted-foreground">
                  to {wishDialog.user?.first_name || wishDialog.user?.last_name || "Friend"}
                </p>
              </div>
            </div>
            
            <textarea
              value={wishMessage}
              onChange={(e) => setWishMessage(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Write your wish message..."
              data-testid="wish-message-input"
            />
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setWishDialog({ open: false, user: null, type: "birthday" })} data-testid="wish-cancel-btn">
                Cancel
              </Button>
              <Button 
                onClick={sendWish} 
                disabled={wishSending || !wishMessage.trim()}
                className={wishDialog.type === "birthday" ? "bg-pink-500 hover:bg-pink-600" : "bg-red-500 hover:bg-red-600"}
                data-testid="wish-send-btn"
              >
                {wishSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1" />}
                Send Wish
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default WelcomePage;
